import React, { ChangeEvent, FormEvent, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { colors } from '@/Theme/LegacyTheme'
import MarketingHelper from '@zuto-internal/zuto-marketing-helper'
import { sendEmail } from '@/utils/ContactUs/sendEmail'
import { CallRequest, ContactRequest, EmailType } from '@/types/EmailType'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

export type FieldConfig = {
  name: string
  label: string
  type?: string
  multiline?: boolean
  rows?: number
  validate?: (value: string) => string | undefined
  fieldType?: 'text' | 'checkbox'
}

type FormFieldProps = {
  field: FieldConfig
  value: string
  error?: string
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  darkTheme?: boolean
}

type CheckboxChangeEvent = ChangeEvent<HTMLInputElement>

const FormCheckboxField = ({ field, value, error, onChange, darkTheme }: FormFieldProps) => {
  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        name: field.name,
        value: String(event.target.checked)
      }
    } as ChangeEvent<HTMLInputElement>

    onChange(syntheticEvent)
  }

  const checkboxColor = darkTheme ? 'white' : colors.blue

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            color={'primary'}
            name={field.name}
            checked={value === 'true'}
            onChange={handleCheckboxChange}
            sx={{
              color: checkboxColor,
              '&.Mui-checked': {
                color: checkboxColor
              },
              '& .MuiSvgIcon-root': { fontSize: 42 }
            }}
          />
        }
        label={field.label}
        sx={{
          mb: 0,
          mt: 0,
          typography: 'body1',
          '.MuiTypography-root': { marginBottom: '0 !important' }
        }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  )
}

const FormTextField = ({ field, value, error, onChange, darkTheme }: FormFieldProps) => {
  const { name, label, type, multiline, rows, fieldType } = field

  if (fieldType === 'checkbox') {
    return (
      <FormCheckboxField
        field={field}
        value={value}
        error={error}
        onChange={onChange}
        darkTheme={darkTheme}
      />
    )
  }

  return (
    <TextField
      name={name}
      label={label}
      type={type}
      value={value}
      onChange={onChange}
      error={Boolean(error)}
      helperText={error}
      multiline={multiline}
      rows={rows}
      fullWidth
      variant="outlined"
      InputProps={{ notched: false }}
      InputLabelProps={{
        shrink: true,
        sx: {
          position: 'relative',
          transform: 'none',
          mb: '0 !important',
          typography: 'body1'
        }
      }}
      sx={{
        '& .MuiFormLabel-root': {
          marginBottom: 1
        },
        '& .MuiOutlinedInput-root': {
          borderRadius: 0,
          mb: 1,
          backgroundColor: 'white'
        },
        '& .Mui-focused > .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
        '& .MuiFormHelperText-root': { m: 0 },
        '& .MuiFormHelperText-root.Mui-error': {
          typography: 'body1',
          pt: 1,
          m: 0,
          ...(darkTheme && { color: `${colors.yellow}` })
        },
        '& .MuiFormLabel-root, & .MuiFormLabel-root.Mui-error': {
          ...(darkTheme && { color: `white !important` })
        }
      }}
    />
  )
}

type DynamicFormProps = {
  fields100Col?: FieldConfig[]
  fieldsLeftCol?: FieldConfig[]
  fieldsRightCol?: FieldConfig[]
  emailType: EmailType
  submitTxt?: string
  darkTheme?: boolean
  isInline?: boolean
}

const DynamicForm = ({
  fields100Col,
  fieldsLeftCol,
  fieldsRightCol,
  emailType,
  submitTxt,
  darkTheme = false,
  isInline = false
}: DynamicFormProps) => {
  const allFields = [...(fieldsLeftCol ?? []), ...(fieldsRightCol ?? []), ...(fields100Col ?? [])]
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(
      allFields.map((field) => [field.name, field.fieldType === 'checkbox' ? 'false' : ''])
    )
  )
  const [errors, setErrors] = useState<Record<string, string | undefined>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)

  const allFieldsFilled = allFields.every((field) => {
    const value = values[field.name] ?? ''

    if (field.fieldType === 'checkbox') {
      return value !== ''
    }

    return value.trim().length > 0
  })

  const allFieldsValid = allFields.every((field) => {
    if (!field.validate) {
      return true
    }

    const value = values[field.name] ?? ''
    return !field.validate(value)
  })

  const hasErrors = Object.values(errors).some(Boolean)
  const isSubmitDisabled = isSubmitting || !allFieldsFilled || !allFieldsValid || hasErrors

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target
    const field = allFields.find((item) => item.name === name)

    setValues((current) => ({ ...current, [name]: value }))

    if (field?.validate) {
      const nextError = field.validate(value)
      setErrors((current) => ({ ...current, [name]: nextError }))
      return
    }

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: undefined }))
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setErrors({})
    setSubmitError(undefined)
    setIsSubmitting(true)

    const marketingDetails = MarketingHelper.getChannelAttribution()
    const body = { ...values, ...marketingDetails } as ContactRequest | CallRequest
    try {
      await sendEmail(emailType, body)
      setIsSubmitted(true)
    } catch {
      setSubmitError('Sorry, something went wrong while sending your request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderTitle = (emailType: EmailType) => {
    if (emailType === 'CallRequest') {
      return (
        <>
          {!isInline && (
            <Typography variant="h1" component="p" sx={{ fontWeight: 400 }}>
              Or we can call you...
            </Typography>
          )}
          <Typography variant="h3" component="h2">
            Arrange a call back
          </Typography>
        </>
      )
    }
    return (
      <>
        <Typography variant="h2" component="h3" mb={6}>
          Write us a message
        </Typography>
      </>
    )
  }

  const renderSuccessMessage = (emailType: EmailType, darkTheme: boolean) => {
    const iconColor = darkTheme ? 'inherit' : 'success'
    return (
      <Box textAlign="left">
        <Typography variant="h2" component="p" mb={6}>
          {emailType === 'CallRequest' ? 'Callback request sent!' : 'Message sent!'}
        </Typography>
        <Typography
          variant="h3"
          component="p"
          mb={2}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}
        >
          <CheckCircleIcon color={iconColor} fontSize="large" />
          <span>Thanks, we&apos;ll be in touch soon</span>
        </Typography>
      </Box>
    )
  }

  const renderField = (field: FieldConfig) => (
    <FormTextField
      key={field.name}
      field={field}
      value={values[field.name]}
      error={errors[field.name]}
      onChange={handleChange}
      darkTheme={darkTheme}
    />
  )

  const submitSection = (
    <Box textAlign="center">
      <Button
        type="submit"
        variant={darkTheme ? 'outlined' : 'contained'}
        size="large"
        disabled={isSubmitDisabled}
        data-za-event-type="click"
        data-za-name="Contact Us - Send Message"
        sx={{ width: 'min(100%, 280px)' }}
      >
        {isSubmitting ? 'Sending...' : submitTxt ?? 'Send Message'}
      </Button>
      {submitError && (
        <Typography color={darkTheme ? 'secondary' : 'error'} sx={{ mt: 2 }}>
          {submitError}
        </Typography>
      )}
    </Box>
  )

  if (isSubmitted) {
    return renderSuccessMessage(emailType, darkTheme)
  }

  if (isInline) {
    return (
      <>
        {renderTitle(emailType)}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={5} alignItems="flex-start">
            <Grid item xs={12} sm>
              <Grid container spacing={3}>
                {(fieldsLeftCol ?? []).map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    {renderField(field)}
                  </Grid>
                ))}
                {(fieldsRightCol ?? []).map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    {renderField(field)}
                  </Grid>
                ))}
                {(fields100Col ?? []).map((field) => (
                  <Grid item xs={12} key={field.name}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm="auto"
              sx={{ mt: { sm: '36px' } }}
            >
              {submitSection}
            </Grid>
          </Grid>
        </Box>
      </>
    )
  }

  return (
    <>
      {renderTitle(emailType)}
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={5}>
          {fieldsLeftCol && (
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>{(fieldsLeftCol ?? []).map(renderField)}</Stack>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Stack spacing={3}>{(fieldsRightCol ?? []).map(renderField)}</Stack>
          </Grid>

          {fields100Col && (
            <Grid item xs={12}>
              <Stack spacing={2}>{fields100Col.map(renderField)}</Stack>
            </Grid>
          )}

          <Grid item xs={12}>
            {submitSection}
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default DynamicForm
